import { useState, useEffect, useRef } from 'react';
import { Box, Typography, Fade } from '@mui/material';
import { keyframes } from '@mui/material/styles';
import TgMonitoringIcon from 'src/icons/TgMonitoringIcon';
import useUserStore from 'src/stores/user';
import useKeywordStore from 'src/stores/keywords';
import useCollectionsStore from 'src/stores/collections';
import useProjectsStore from 'src/stores/projects';

const pulse = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(127,48,228,0.1), 0 0 12px 1px #FA00FF11; }
  70% { box-shadow: 0 0 0 20px rgba(127,48,228,0); }
  100% { box-shadow: 0 0 0 0 rgba(127,48,228,0.1), 0 0 12px 1px #FA00FF11; }
`;

const dotsAnimation = keyframes`
  0%, 20% { opacity: 0; }
  40% { opacity: 1; }
  60%, 100% { opacity: 0; }
`;

const ONBOARDING_MESSAGES = {
    email_confirm: 'Подтвердите почту для продолжения настройки сервиса',
    description: 'Расскажите о своём бизнесе для AI',
    choose_plan: 'Выберите тариф и запустите круглосуточный поиск лидов',
    choose_collections: 'Подборки - это готовые наборы чатов. Выберите подходящие подборки для вашего бизнеса',
    add_chats: 'Добавьте чаты если хотите мониторить конкретные чаты',
    add_keywords: 'Мониторинг остановлен. Добавьте ключевые слова - по ним будут искаться лиды',
    completed: 'Настройка завершена! Мониторинг активен',
};

export default function AIMonitoringBlock() {
    const [logIndex, setLogIndex] = useState(() => Math.floor(Math.random() * 4));
    const [prevLogIndex, setPrevLogIndex] = useState<number | null>(null);
    const [fade, setFade] = useState(true);
    const intervalRef = useRef<NodeJS.Timeout | null>(null);
    const isInitializedRef = useRef(false);

    const isOnboardingCompleted = useUserStore(({ isOnboardingCompleted }) => isOnboardingCompleted);
    const onboardingStep = useUserStore(({ onboardingStep }) => onboardingStep);
    const current_subscription = useUserStore((state) => state.current_subscription);
    const keywords = useKeywordStore(({ keywords }) => keywords);
    const { privateCollection } = useCollectionsStore(({ privateCollection }) => ({ privateCollection }));
    const { activeProject } = useProjectsStore(({ activeProject }) => ({ activeProject }));

    // Определяем статус готовности
    const hasSubscription = !!current_subscription;
    const hasKeywords = keywords.length > 0;
    const hasChats = privateCollection?.chats && privateCollection.chats.length > 0;
    const hasBundles = activeProject?.bundles && activeProject.bundles.filter((b) => b.is_public).length > 0;
    const hasChatsOrBundles = hasChats || hasBundles;
    const hasProject = activeProject && activeProject.id > 0; // Проверяем что проект создан
    const isReady = hasSubscription && hasKeywords && hasChatsOrBundles && hasProject;

    // Получаем текущий шаг онбординга
    const getCurrentOnboardingStep = () => {
        const steps = [
            'email_confirm',
            'description',
            'choose_collections',
            'add_chats',
            'add_keywords',
            'choose_plan',
            'completed',
        ];
        return steps[onboardingStep - 1] || 'completed';
    };

    // Генерируем сообщения для активного мониторинга
    const getActiveMessages = () => {
        // Если проект не создан, возвращаем пустой массив
        if (!hasProject) {
            return [];
        }

        const messages = ['Фильтрую спам сообщения', 'Обрабатываю новые сообщения'];
        if (hasChats) {
            const chats = privateCollection?.chats || [];
            if (chats.length > 0) {
                const firstChat = chats[0];
                const chatName = firstChat.name || firstChat.telegram_link || 'чат';
                if (chats.length === 1) {
                    messages.push(`Мониторю чат "${chatName}" на лиды`);
                } else if (chats.length === 2) {
                    const secondChat = chats[1];
                    const secondChatName = secondChat.name || secondChat.telegram_link || 'чат';
                    messages.push(`Проверяю "${chatName}" и "${secondChatName}" на лиды`);
                } else {
                    messages.push(
                        `Мониторю ${chatName} и еще ${chats.length - 1} чат${chats.length - 1 > 1 ? 'ов' : ''} на лиды`
                    );
                }
            }
        }
        if (hasBundles) {
            const bundles = activeProject?.bundles?.filter((b) => b.is_public) || [];
            if (bundles.length > 0) {
                bundles.forEach((bundle) => {
                    const bundleName = bundle.name || 'подборка';
                    messages.push(`Мониторю подборку "${bundleName}" на лиды`);
                });
            }
        }
        if (hasKeywords) {
            const keywordList = keywords.map((k) => k.word);
            if (keywordList.length === 1) {
                messages.push(`Ищу лиды по ключевому слову "${keywordList[0]}"`);
            } else {
                keywordList.forEach((keyword) => {
                    messages.push(`Ищу лиды по слову "${keyword}"`);
                });
            }
        }
        messages.push('Обновляю статистику найденных лидов');
        return messages;
    };

    // Получаем подсказку в зависимости от статуса
    const getHintMessage = () => {
        if (!isOnboardingCompleted) {
            const currentStep = getCurrentOnboardingStep();
            let msg = ONBOARDING_MESSAGES[currentStep as keyof typeof ONBOARDING_MESSAGES] || 'Завершите настройку';
            if (msg === 'Запуск мониторинга') msg = 'Идёт поиск лидов';
            return msg;
        }
        if (!hasSubscription) {
            return 'Выберите тариф и начнется мониторинг лидов в реальном времени';
        }
        if (!hasKeywords) {
            return 'Мониторинг остановлен. Добавьте ключевые слова - по ним будут искаться лиды';
        }
        if (!hasChatsOrBundles) {
            return 'Выберите где искать лиды: добавьте чаты или выберите готовые подборки';
        }
        if (!hasProject) {
            return 'Создайте проект для запуска мониторинга';
        }
        return null; // Всё готово
    };

    useEffect(() => {
        if (!isInitializedRef.current) {
            isInitializedRef.current = true;
        }
        if (isReady) {
            const activeMessages = getActiveMessages();
            if (activeMessages.length > 0) {
                intervalRef.current = setInterval(() => {
                    setFade(false);
                    setTimeout(() => {
                        let randomIndex = Math.floor(Math.random() * activeMessages.length);
                        if (activeMessages.length > 1 && prevLogIndex !== null) {
                            // Исключаем повтор
                            while (randomIndex === prevLogIndex) {
                                randomIndex = Math.floor(Math.random() * activeMessages.length);
                            }
                        }
                        setLogIndex(randomIndex);
                        setPrevLogIndex(randomIndex);
                        setFade(true);
                    }, 350);
                }, 4000);
            }
        } else {
            const activeMessages = getActiveMessages();
            if (activeMessages.length > 0) {
                const randomIndex = Math.floor(Math.random() * activeMessages.length);
                setLogIndex(randomIndex);
                setPrevLogIndex(randomIndex);
            }
        }
        return () => {
            if (intervalRef.current) clearInterval(intervalRef.current);
        };
    }, [isReady, hasChats, hasBundles, hasKeywords, hasProject]);

    const hintMessage = getHintMessage();
    const activeMessages = getActiveMessages();
    const isActive = isReady;

    return (
        <Box
            mb={1}
            p={1.2}
            minHeight={101}
            sx={{
                bgcolor: 'var(--lm-surface)',
                border: '1px solid var(--lm-border)',
                borderRadius: '8px',
                boxShadow: '0 6px 18px rgba(17, 24, 39, 0.04)',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                position: 'relative',
                overflow: 'hidden',
                minHeight: 85,
                maxHeight: 117,
            }}
        >
            <Box
                sx={{
                    background: isActive ? 'linear-gradient(135deg, #FA00FF33 0%, #FF1F0033 100%)' : 'none',
                    borderRadius: '50%',
                    p: 0.7,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mr: 1,
                    animation: isActive ? `${pulse} 5s infinite` : 'none',
                }}
            >
                <TgMonitoringIcon />
            </Box>
            <Box
                flex={1}
                minWidth={0}
            >
                <Typography
                    variant="body2"
                    fontWeight={600}
                    sx={{
                        fontSize: 14,
                        wordBreak: 'break-word',
                        color: 'text.primary',
                    }}
                >
                    {isActive ? (
                        <Fade
                            in={fade}
                            timeout={{ enter: 350, exit: 350 }}
                        >
                            <span>
                                {activeMessages[logIndex]}
                                <span style={{ display: 'inline-block', marginLeft: 4 }}>
                                    <span
                                        style={{
                                            animation: `${dotsAnimation} 1.4s infinite`,
                                            animationDelay: '0s',
                                            display: 'inline-block',
                                            fontSize: '14px',
                                            color: 'inherit',
                                        }}
                                    >
                                        .
                                    </span>
                                    <span
                                        style={{
                                            animation: `${dotsAnimation} 1.4s infinite`,
                                            animationDelay: '0.2s',
                                            display: 'inline-block',
                                            fontSize: '14px',
                                            color: 'inherit',
                                        }}
                                    >
                                        .
                                    </span>
                                    <span
                                        style={{
                                            animation: `${dotsAnimation} 1.4s infinite`,
                                            animationDelay: '0.4s',
                                            display: 'inline-block',
                                            fontSize: '14px',
                                            color: 'inherit',
                                        }}
                                    >
                                        .
                                    </span>
                                </span>
                            </span>
                        </Fade>
                    ) : (
                        <span>{hintMessage || 'Мониторинг неактивен'}</span>
                    )}
                </Typography>
            </Box>
        </Box>
    );
}
